export type WorkOrderStatus = "pending" | "in_progress" | "completed" | "on_hold" | "canceled";
export type WorkOrderTransitionStatus = "in_progress" | "on_hold" | "completed" | "canceled";
export type WorkOrderPriority = "low" | "medium" | "high" | "urgent";

export const WORK_ORDER_STATUS_LABELS: Record<WorkOrderStatus, string> = {
  pending: "Pending",
  in_progress: "In Progress",
  on_hold: "On Hold",
  completed: "Completed",
  canceled: "Canceled",
};

export interface WorkOrder {
  id: string;
  boat_id: string;
  boat_name: string;
  title: string;
  description: string;
  status: WorkOrderStatus;
  priority: WorkOrderPriority;
  assigned_technician: string;
  scheduled_date: string | null;
  due_date: string | null;
  completed_at: string | null;
  schedule_id: string | null;
  parts_cost: number;
  labor_hours: number;
  labor_rate: number;
  labor_cost: number;
  created_at: string;
  updated_at: string;
}

export interface WorkOrderWriteBody {
  // Client-generated on create so offline creates are replay-idempotent and
  // addressable before the server responds (backend ignores it on update).
  id?: string;
  boat_id: string;
  title: string;
  description?: string;
  priority?: WorkOrderPriority;
  assigned_technician?: string;
  scheduled_date?: string;
  due_date?: string;
  schedule_id?: string;
  parts_cost?: number;
  labor_hours?: number;
  labor_rate?: number;
  // Payer for AR service invoices (finance module); empty/undefined = no payer.
  customer_id?: string;
  // Customer-facing hourly rate (undefined = fall back to labor_rate).
  billable_labor_rate?: number;
}

export interface ServiceSchedule {
  id: string;
  boat_id: string;
  title: string;
  description: string;
  interval_days: number;
  interval_hours?: number | null;
  last_performed_at: string | null;
  last_performed_hours?: number | null;
  next_due_at: string;
  next_due_hours?: number | null;
  reminder_lead_days: number;
  auto_create_work_order: boolean;
  priority: WorkOrderPriority;
  assigned_technician: string;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface ServiceScheduleWriteBody {
  id?: string; // client-generated for offline create replay idempotency
  boat_id: string;
  title: string;
  description?: string;
  interval_days: number;
  interval_hours?: number;
  last_performed_at?: string;
  next_due_at?: string;
  reminder_lead_days?: number;
  auto_create_work_order?: boolean;
  priority?: WorkOrderPriority;
  assigned_technician?: string;
  active?: boolean;
}

export interface ServiceSummary {
  boats_in_service: number;
  open_work_orders: number;
  due_within_7_days: number;
  overdue: number;
  boats: BoatStatusRow[];
}

export interface BoatStatusRow {
  boat_id: string;
  make: string;
  model: string;
  year: number;
  open_work_orders: number;
  urgent_or_high: number;
  next_due_at: string | null;
  earliest_open_due: string | null;
}

// --- Reminders ---
// Mirrors the Go typed-strings in internal/modules/service/domain/reminder.go
// and the Postgres enums reminder_due_kind / reminder_status.
export type ReminderDueKind = "time" | "hours";
export type ReminderStatus = "sent" | "skipped" | "failed";

export interface ServiceReminder {
  id: string;
  schedule_id: string;
  boat_id: string;
  customer_id?: string | null;
  schedule_title: string;
  boat_name: string;
  customer_name: string;
  due_kind: ReminderDueKind;
  due_marker: string;
  status: ReminderStatus;
  work_order_id?: string | null;
  note?: string;
  created_at: string;
}

export interface ReminderRunStats {
  scanned: number;
  triggered: number;
  skipped: number;
  sent: number;
  no_contact: number;
  failed: number;
}

// --- Manufacturer reference data ---
// Mirrors the embedded dataset in internal/shared/enginedata.

export type ServiceSourceType = "official" | "dealer" | "community";

export interface ReferenceServiceItem {
  key: string;
  title: string;
  description: string;
  interval_hours: number | null;
  interval_months: number | null;
  priority: WorkOrderPriority;
  source_url: string;
  source_type: ServiceSourceType;
}

export interface ReferenceKnownIssue {
  summary: string;
  severity: string;
  details: string;
  source_url: string;
}

export interface ReferenceRecall {
  title: string;
  affected: string;
  opened: string;
  source_url: string;
}

export interface ReferenceManufacturer {
  key: string;
  name: string;
  engine_type: string;
  items: ReferenceServiceItem[];
  known_issues: ReferenceKnownIssue[];
  recalls: ReferenceRecall[];
}

export interface ReferenceIndex {
  manufacturers: {
    key: string;
    name: string;
    engine_type: string;
    item_count: number;
  }[];
  boat_make_engine_hints: Record<string, string>;
  uscg_recalls_url: string;
}

export interface ApplyPlanResult {
  manufacturer_key: string;
  created: ServiceSchedule[];
  skipped: string[];
}
